import { ResponsiveBar } from '@nivo/bar';
import { UserRankingDto } from '@repo/shared/dtos/analytics/UtilizationDto';
import { CHART_THEMES } from '../config';
import { formatNumber } from '../utils/dataUtils';
import { ChartEmptyState } from './shared';
import { palette } from '../shared/theme';
import { useResponsiveChartConfig } from '../shared/useResponsiveChartConfig';

interface UserRankingChartProps {
  data: UserRankingDto[];
  height?: number;
  showSessions?: boolean;
  showMinutes?: boolean;
  maxItems?: number;
  onBarClick?: (user: UserRankingDto) => void;
}

function getDisplayName(firstName: string, lastName: string): string {
  const fullName = `${firstName} ${lastName}`;
  if (fullName.length <= 20) return fullName;

  return `${firstName} ${lastName[0]}.`;
}

export function UserRankingChart({
  data,
  height = 300,
  showSessions = true,
  showMinutes = false,
  maxItems = 10,
  onBarClick,
}: UserRankingChartProps) {
  const { isMobile } = useResponsiveChartConfig();

  if (!data?.length) {
    return <ChartEmptyState message="No user data available" height={height} />;
  }

  // Reduce bar count on mobile so each bar has enough width to render clearly
  const effectiveMaxItems = isMobile ? Math.min(maxItems, 7) : maxItems;

  const chartData = data.slice(0, effectiveMaxItems).map((user, index) => {
    const userName = `${user.first_name} ${user.last_name}`;
    return {
      user: getDisplayName(user.first_name, user.last_name),
      fullName: userName,
      sessions: user.total_sessions,
      minutes: Math.round(user.total_minutes),
      itemIndex: index,
    };
  });

  const keys: string[] = [];
  if (showSessions) keys.push('sessions');
  if (showMinutes) keys.push('minutes');

  const handleClick = onBarClick
    ? (clickData: any) => {
        const originalUser = data[clickData.data.itemIndex];
        if (originalUser) {
          onBarClick(originalUser);
        }
      }
    : undefined;

  const chartMargins = {
    top: 15,
    right: isMobile ? 20 : showSessions && showMinutes ? 100 : 50,
    // Extra bottom margin on mobile: rotated tick labels need ~80px, legend (if present) needs more
    bottom: isMobile ? 90 : 80,
    left: isMobile ? 40 : 70,
  };

  return (
    <div style={{ height }}>
      <ResponsiveBar
        data={chartData}
        keys={keys}
        indexBy="user"
        margin={chartMargins}
        padding={0.3}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        colors={[palette.indigo, palette.red]}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 8,
          tickRotation: -45,
          legendOffset: 50,
          legendPosition: 'middle',
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: isMobile ? 4 : 8,
          tickRotation: 0,
          legend: showSessions && showMinutes ? 'Count' : showSessions ? 'Sessions' : 'Minutes',
          legendOffset: isMobile ? -35 : -60,
          legendPosition: 'middle',
        }}
        enableGridY={true}
        enableLabel={false}
        animate={true}
        motionConfig="gentle"
        onClick={handleClick}
        legends={
          showSessions && showMinutes
            ? [
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  justify: false,
                  translateX: 100,
                  translateY: -20,
                  itemsSpacing: 8,
                  itemDirection: 'left-to-right',
                  itemWidth: 80,
                  itemHeight: 20,
                  itemOpacity: 0.9,
                  symbolSize: 14,
                  symbolShape: 'circle',
                  symbolBorderColor: 'rgba(0, 0, 0, .2)',
                  itemTextColor: palette.text,
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemBackground: 'rgba(0, 0, 0, .05)',
                        itemOpacity: 1,
                      },
                    },
                  ],
                },
              ]
            : []
        }
        tooltip={({ id, value, color, data }: any) => (
          <div className="max-w-xs rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] p-3 text-sm text-[var(--anl-text)]">
            <div className="mb-2 font-medium" title={data.fullName}>
              {data.fullName}
            </div>
            <div className="flex items-center space-x-2">
              <div
                className="h-4 w-4 rounded border-2 border-[var(--anl-surface-elev)] shadow-sm"
                style={{ backgroundColor: color }}
              />
              <span className="font-medium capitalize">{id}:</span>
              <span className="font-bold">{formatNumber(value)}</span>
            </div>
            {onBarClick && (
              <div className="mt-2 text-xs italic text-[var(--anl-text-muted)]">
                Click to view details
              </div>
            )}
          </div>
        )}
        theme={CHART_THEMES.default}
      />
    </div>
  );
}
